/**
 * 分组数据缓存服务（Web 应用版本）
 * 用于缓存分组列表和成员列表，减少网络请求
 */

import type { Group, GroupMemberWithProfile } from '@ext-bookmark/shared/types';

const CACHE_KEYS = {
  USER_GROUPS: 'user_groups_cache',
  GROUP_MEMBERS_PREFIX: 'group_members_cache_',
} as const;

// 缓存过期时间（毫秒）：5分钟
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

interface CachedData<T> {
  data: T;
  timestamp: number;
}

/**
 * 检查缓存是否有效
 */
function isCacheValid<T>(cached: CachedData<T> | null): cached is CachedData<T> {
  if (!cached) return false;
  const now = Date.now();
  return (now - cached.timestamp) < CACHE_EXPIRY_MS;
}

/**
 * 获取用户分组列表缓存
 */
export async function getUserGroupsCache(): Promise<Group[] | null> {
  try {
    const cachedStr = localStorage.getItem(CACHE_KEYS.USER_GROUPS);
    if (!cachedStr) return null;
    
    const cached = JSON.parse(cachedStr) as CachedData<Group[]>;
    
    if (isCacheValid(cached)) {
      return cached.data;
    }
    
    // 缓存过期，清除旧缓存
    localStorage.removeItem(CACHE_KEYS.USER_GROUPS);
    
    return null;
  } catch (error) {
    console.warn('[GroupCache] 读取分组列表缓存失败:', error);
    return null;
  }
}

/**
 * 设置用户分组列表缓存
 */
export async function setUserGroupsCache(groups: Group[]): Promise<void> {
  try {
    const cached: CachedData<Group[]> = {
      data: groups,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEYS.USER_GROUPS, JSON.stringify(cached));
  } catch (error) {
    console.warn('[GroupCache] 设置分组列表缓存失败:', error);
  }
}

/**
 * 清除用户分组列表缓存
 */
export async function clearUserGroupsCache(): Promise<void> {
  try {
    localStorage.removeItem(CACHE_KEYS.USER_GROUPS);
  } catch (error) {
    console.warn('[GroupCache] 清除分组列表缓存失败:', error);
  }
}

/**
 * 获取分组成员列表缓存
 */
export async function getGroupMembersCache(groupId: string): Promise<GroupMemberWithProfile[] | null> {
  try {
    const cacheKey = `${CACHE_KEYS.GROUP_MEMBERS_PREFIX}${groupId}`;
    const cachedStr = localStorage.getItem(cacheKey);
    if (!cachedStr) return null;
    
    const cached = JSON.parse(cachedStr) as CachedData<GroupMemberWithProfile[]>;
    
    if (isCacheValid(cached)) {
      return cached.data;
    }
    
    // 缓存过期，清除旧缓存
    localStorage.removeItem(cacheKey);
    
    return null;
  } catch (error) {
    console.warn('[GroupCache] 读取成员列表缓存失败:', error);
    return null;
  }
}

/**
 * 设置分组成员列表缓存
 */
export async function setGroupMembersCache(groupId: string, members: GroupMemberWithProfile[]): Promise<void> {
  try {
    const cacheKey = `${CACHE_KEYS.GROUP_MEMBERS_PREFIX}${groupId}`;
    const cached: CachedData<GroupMemberWithProfile[]> = {
      data: members,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cached));
  } catch (error) {
    console.warn('[GroupCache] 设置成员列表缓存失败:', error);
  }
}

/**
 * 清除分组成员列表缓存
 */
export async function clearGroupMembersCache(groupId: string): Promise<void> {
  try {
    const cacheKey = `${CACHE_KEYS.GROUP_MEMBERS_PREFIX}${groupId}`;
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.warn('[GroupCache] 清除成员列表缓存失败:', error);
  }
}

/**
 * 清除所有分组相关缓存
 */
export async function clearAllGroupCache(): Promise<void> {
  try {
    await clearUserGroupsCache();
    // 注意：这里不清理所有成员缓存，因为需要知道所有 groupId
    // 如果需要清理所有成员缓存，可以在需要时单独调用
  } catch (error) {
    console.warn('[GroupCache] 清除所有分组缓存失败:', error);
  }
}
